import { DateTime } from "luxon";

// The outage form displays and submits every datetime as Eastern wall clock —
// OutageSetup reinterprets the picker values as America/New_York before sending UTC.
export const FORM_TIME_ZONE = "America/New_York";

// AMP stores asset.time_zone_code as a standard/daylight abbreviation (dbo.TIME_ZONE),
// not an IANA zone. Map to IANA so the offset comes from the outage's own date and
// DST is handled instead of being baked into the abbreviation.
const IANA_BY_TIME_ZONE_CODE: Record<string, string> = {
  PST: "America/Los_Angeles",
  PDT: "America/Los_Angeles",
  MST: "America/Denver",
  MDT: "America/Denver",
  CST: "America/Chicago",
  CDT: "America/Chicago",
  EST: "America/New_York",
  EDT: "America/New_York",
};

interface TimeZoneLookupResource {
  resource_id: number;
}

interface TimeZoneLookupAsset {
  asset_id: number;
  time_zone_code?: string | null;
  resources?: TimeZoneLookupResource[];
}

export const resolveIanaZone = (timeZoneCode?: string | null): string | null => {
  if (!timeZoneCode) return null;
  return IANA_BY_TIME_ZONE_CODE[timeZoneCode.trim().toUpperCase()] ?? null;
};

// Resources carry no timezone of their own, so a Resource reference inherits the
// timezone of the asset it hangs off.
export const findReferenceTimeZoneCode = (
  assets: TimeZoneLookupAsset[],
  referenceId: number | "" | null | undefined,
  referenceTypeCode?: string | null
): string | null => {
  if (referenceId === "" || referenceId === null || referenceId === undefined) return null;

  const id = Number(referenceId);
  if (Number.isNaN(id)) return null;

  if (referenceTypeCode === "Resource") {
    const parentAsset = assets.find((asset) =>
      (asset.resources ?? []).some((resource) => resource.resource_id === id)
    );
    return parentAsset?.time_zone_code ?? null;
  }

  return assets.find((asset) => asset.asset_id === id)?.time_zone_code ?? null;
};

export const formatWallClock = (date: Date): string => {
  const pad = (value: number) => value.toString().padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
};

// Reads wallClock's calendar fields as a time in the asset's zone and returns the
// equivalent Eastern wall clock. 2026-07-28 23:59 on a PST (CAISO) asset becomes
// 2026-07-29 02:59 — the date rolls because Pacific trails Eastern by three hours.
export const convertAssetLocalToFormZone = (
  wallClock: Date,
  timeZoneCode?: string | null
): Date | null => {
  const zone = resolveIanaZone(timeZoneCode);
  if (!zone) return null;

  const inAssetZone = DateTime.fromObject(
    {
      year: wallClock.getFullYear(),
      month: wallClock.getMonth() + 1,
      day: wallClock.getDate(),
      hour: wallClock.getHours(),
      minute: wallClock.getMinutes(),
      second: 0,
      millisecond: 0,
    },
    { zone }
  );

  if (!inAssetZone.isValid) return null;

  const inFormZone = inAssetZone.setZone(FORM_TIME_ZONE);

  // Rebuild as a browser-local Date so the pickers show the Eastern numbers,
  // which is what OutageSetup expects to hand back to the API.
  return new Date(
    inFormZone.year,
    inFormZone.month - 1,
    inFormZone.day,
    inFormZone.hour,
    inFormZone.minute,
    0,
    0
  );
};
